import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import axios from 'axios';
import {
  Mail,
  Lock,
  LogIn,
  Eye,
  EyeOff,
  UserRound,
  X,
  Trash2,
  Settings2,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import {
  accountDisplayName,
  accountInitial,
  DEFAULT_MAIL_SERVER,
  getRememberedAccounts,
  normalizeMailServer,
  removeRememberedAccount,
} from '../utils/rememberedAccount';
import {
  detectMailProvider,
  MAIL_PROVIDERS,
  providerToMailServer,
} from '../utils/mailProviders';

const emptyMailForm = () => ({
  imapHost: DEFAULT_MAIL_SERVER.imapHost,
  smtpHost: DEFAULT_MAIL_SERVER.smtpHost,
  imapPort: String(DEFAULT_MAIL_SERVER.imapPort),
  smtpPort: String(DEFAULT_MAIL_SERVER.smtpPort),
});

const mailFormFromServer = (mail) => ({
  imapHost: mail?.imapHost || DEFAULT_MAIL_SERVER.imapHost,
  smtpHost: mail?.smtpHost || DEFAULT_MAIL_SERVER.smtpHost,
  imapPort: String(mail?.imapPort || DEFAULT_MAIL_SERVER.imapPort),
  smtpPort: String(mail?.smtpPort || DEFAULT_MAIL_SERVER.smtpPort),
});

const GoogleIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="#EA4335"
      d="M12 10.2v3.6h5.1c-.2 1.2-.9 2.2-1.9 2.9l3.1 2.4c1.8-1.7 2.8-4.1 2.8-7 0-.7-.1-1.3-.2-1.9H12z"
    />
    <path
      fill="#34A853"
      d="M5.3 14.3l-.8.6-2.5 1.9C3.5 20.1 7.4 22.5 12 22.5c2.7 0 5-.9 6.7-2.4l-3.1-2.4c-.9.6-2 1-3.6 1-2.8 0-5.1-1.9-5.9-4.4z"
    />
    <path
      fill="#4A90E2"
      d="M3.9 6.2C3.3 7.4 3 8.7 3 10s.3 2.6.9 3.8c0 .1 3.2-2.5 3.2-2.5C6.8 10.4 6.7 9.5 6.7 9c0-.5.1-1 .2-1.5L3.9 6.2z"
    />
    <path
      fill="#FBBC05"
      d="M12 5.3c1.5 0 2.8.5 3.8 1.5l2.8-2.8C16.9 2.3 14.7 1.5 12 1.5 7.4 1.5 3.5 3.9 1.9 7.7l3.2 2.5C6.9 7.2 9.2 5.3 12 5.3z"
    />
  </svg>
);


const Login = () => {
  const { login, completeGoogleLogin, isAuthenticated, loading } = useAuth();
  const { isDarkMode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const [accounts, setAccounts] = useState(() => getRememberedAccounts());
  const [mode, setMode] = useState(() => (getRememberedAccounts().length ? 'picker' : 'form'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mailForm, setMailForm] = useState(emptyMailForm);
  const [mailManual, setMailManual] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showBusyLabel, setShowBusyLabel] = useState(false);
  const [activeAccountId, setActiveAccountId] = useState(null);
  const [googleEnabled, setGoogleEnabled] = useState(false);
  const [googleStartUrl, setGoogleStartUrl] = useState('');
  const [accountToRemove, setAccountToRemove] = useState(null);
  const submittingRef = useRef(false);
  const oauthHandledRef = useRef(false);

  const detectedProvider = useMemo(() => detectMailProvider(email), [email]);

  const subtitle = useMemo(() => {
    if (mode === 'picker') return 'Gespeicherten Account wählen';
    if (googleEnabled) return 'Mit Google oder E-Mail-Konto anmelden';
    return 'Mit E-Mail-Konto anmelden';
  }, [mode, googleEnabled]);

  // Kurze Failures (Server down) sollen nicht mit „Anmelden…“ flackern
  useEffect(() => {
    if (!submitting) {
      setShowBusyLabel(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setShowBusyLabel(true), 320);
    return () => window.clearTimeout(timer);
  }, [submitting]);

  const ensureApiReachable = async () => {
    const maxAttempts = 8;
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      try {
        const res = await axios.get('/api/health', { timeout: 3000 });
        if (res.data && res.data.ready === false) {
          if (attempt < maxAttempts) {
            await new Promise((r) => setTimeout(r, 700));
            continue;
          }
          toast.error('Server startet noch. Bitte kurz warten und erneut versuchen.', {
            duration: 5000,
          });
          return false;
        }
        return true;
      } catch {
        if (attempt < maxAttempts) {
          await new Promise((r) => setTimeout(r, 700));
          continue;
        }
        toast.error('Server nicht erreichbar. Bitte kurz warten und erneut versuchen.', {
          duration: 5000,
        });
        return false;
      }
    }
    return false;
  };

  // Google-Status nicht pollen — sonst Proxy-Spam beim Server-Start/HMR.
  // Wird bei Bedarf über den Google-Button nachgeladen.
  useEffect(() => {
    if (oauthHandledRef.current || loading) return;
    const params = new URLSearchParams(location.search || '');
    const googleError = params.get('google_error');
    const googleCode = params.get('google_code');
    if (googleError) {
      oauthHandledRef.current = true;
      setMode('form');
      toast.error(googleError, { duration: 7000 });
      navigate('/login', { replace: true });
      return;
    }

    if (googleCode) {
      oauthHandledRef.current = true;
      setMode('form');
      (async () => {
        setSubmitting(true);
        try {
          await completeGoogleLogin(googleCode);
          setAccounts(getRememberedAccounts());
          toast.success('Mit Google angemeldet');
          navigate('/', { replace: true });
        } catch (error) {
          toast.error(
            error.response?.data?.error || error.message || 'Google-Anmeldung fehlgeschlagen',
            { duration: 6000 }
          );
          navigate('/login', { replace: true });
        } finally {
          setSubmitting(false);
        }
      })();
      return;
    }

  }, [completeGoogleLogin, loading, location.search, navigate]);

  if (isAuthenticated) {
    const redirectTo = location.state?.from?.pathname || '/';
    return <Navigate to={redirectTo} replace />;
  }

  const applyProviderToForm = (provider) => {
    if (!provider) return;
    setMailForm(mailFormFromServer(providerToMailServer(provider)));
    setMailManual(false);
  };

  const resolveMailForLogin = (loginEmail, mailInput) => {
    const provider = detectMailProvider(loginEmail);
    let mail = normalizeMailServer(mailInput);
    const isDefaultHosting = !mail.imapHost;

    if (provider && isDefaultHosting) {
      mail = normalizeMailServer(providerToMailServer(provider));
    }
    return { mail, provider };
  };

  const performLogin = async (loginEmail, loginPassword, mailInput = {}) => {
    const normalizedEmail = String(loginEmail || '').trim();
    const normalizedPassword = String(loginPassword || '');
    if (!normalizedEmail || !normalizedPassword) {
      toast.error('E-Mail und Passwort eingeben');
      return false;
    }

    const provider = detectMailProvider(normalizedEmail);
    // Gmail: normales Passwort funktioniert nicht per IMAP — bei aktivem OAuth → Google,
    // sonst App-Passwort im Passwortfeld (kein Google-Button).
    if (provider?.id === 'gmail' && googleEnabled) {
      toast('Gmail: Anmeldung über Google…', { icon: '🔐', duration: 2500 });
      await startGoogleLogin();
      return false;
    }
    if (/@(outlook|hotmail|live|msn)\./i.test(normalizedEmail)) {
      toast.error(
        'Hotmail/Outlook wird nicht unterstützt. Bitte Gmail, WEB.DE, GMX o. Ä. nutzen.',
        { duration: 7000 }
      );
      return false;
    }
    if (/@(icloud|me|mac)\./i.test(normalizedEmail) || /@(icloud|me|mac)$/i.test(normalizedEmail)) {
      toast.error(
        'iCloud wird nicht unterstützt. Bitte Gmail, WEB.DE, GMX o. Ä. nutzen.',
        { duration: 7000 }
      );
      return false;
    }

    const { mail } = resolveMailForLogin(normalizedEmail, mailInput);
    if (!mail.imapHost || !mail.smtpHost) {
      toast.error('IMAP- und SMTP-Server eingeben');
      return false;
    }
    if (submittingRef.current) return false;
    if (!(await ensureApiReachable())) {
      setActiveAccountId(null);
      return false;
    }
    submittingRef.current = true;
    setSubmitting(true);
    try {
      await login(normalizedEmail, normalizedPassword, mail);
      setAccounts(getRememberedAccounts());
      return true;
    } catch (error) {
      const data = error.response?.data;
      const msg =
        data?.error ||
        (error.code === 'ECONNABORTED'
          ? 'Anmeldung dauert zu lange (Mail-Server). Bitte erneut versuchen.'
          : error.message) ||
        'Login fehlgeschlagen';
      toast.error(msg, { duration: 7000 });
      return false;
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
      setActiveAccountId(null);
    }
  };

  const startGoogleLoginRedirect = async () => {
    try {
      const res = await axios.get('/api/auth/google/url');
      const url = String(res.data?.url || '').trim();
      if (url) {
        window.location.href = url;
        return;
      }
      if (googleStartUrl) {
        window.location.href = googleStartUrl;
        return;
      }
      window.location.href = '/api/auth/google';
    } catch (error) {
      setSubmitting(false);
      toast.error(
        error.response?.data?.error || error.message || 'Google-Anmeldung konnte nicht gestartet werden',
        { duration: 7000 }
      );
    }
  };

  const startGoogleLogin = async () => {
    let enabled = googleEnabled;
    // Falls Status noch nicht nachgeladen: einmal sofort prüfen (Klick ist selten)
    if (!enabled) {
      try {
        const res = await axios.get('/api/auth/google/status', { timeout: 5000 });
        enabled = !!res.data?.enabled;
        setGoogleEnabled(enabled);
        setGoogleStartUrl(String(res.data?.startUrl || '').trim());
      } catch {
        toast.error('Server noch nicht erreichbar. Bitte kurz warten und erneut versuchen.', {
          duration: 5000,
        });
        return;
      }
    }
    if (!enabled) {
      toast.error(
        'Google-Anmeldung ist noch nicht eingerichtet. In der .env GOOGLE_CLIENT_ID und GOOGLE_CLIENT_SECRET setzen, Server neu starten.',
        { duration: 8000 }
      );
      return;
    }

    // Redirect-Flow (zuverlässiger als Popup — gleiche Redirect-URI wie in der Google Console)
    setSubmitting(true);
    await startGoogleLoginRedirect();
  };


  const selectAccount = async (account) => {
    if (account?.authType === 'google') {
      setActiveAccountId(account.id);
      startGoogleLogin();
      return;
    }
    if (account?.email && account?.password) {
      setActiveAccountId(account.id);
      await performLogin(account.email, account.password, account.mail);
      return;
    }
    setEmail(account?.email || '');
    setPassword('');
    setMailForm(mailFormFromServer(account?.mail));
    setMailManual(Boolean(account?.mail?.imapHost));
    setShowAdvanced(false);
    setMode('form');
    setActiveAccountId(null);
  };

  const useDifferentAccount = () => {
    setEmail('');
    setPassword('');
    setMailForm(emptyMailForm());
    setMailManual(false);
    setShowPassword(false);
    setShowAdvanced(false);
    setMode('form');
  };

  const requestRemoveAccount = (e, account) => {
    e?.stopPropagation?.();
    setAccountToRemove(account);
  };

  const confirmRemoveAccount = () => {
    const account = accountToRemove;
    if (!account) return;
    setAccountToRemove(null);
    const next = removeRememberedAccount(account.email, account.mail?.imapHost);
    setAccounts(next);
    if (!next.length) setMode('form');
    toast.success('Account entfernt');
  };

  const handleEmailChange = (value) => {
    setEmail(value);
    if (mailManual) return;
    const provider = detectMailProvider(value);
    if (provider) {
      applyProviderToForm(provider);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await performLogin(email, password, mailForm);
  };

  const updateMailField = (field, value) => {
    setMailManual(true);
    setMailForm((prev) => ({ ...prev, [field]: value }));
  };

  const applyPreset = (provider) => {
    applyProviderToForm(provider);
  };

  const GoogleButton = (
    <button
      type="button"
      onClick={startGoogleLogin}
      disabled={submitting}
      className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm font-medium text-neutral-800 dark:text-neutral-100 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors disabled:opacity-50"
      title={
        googleEnabled
          ? 'Normal mit Google-Konto anmelden'
          : 'Noch nicht konfiguriert — GOOGLE_CLIENT_ID in .env setzen'
      }
    >
      <GoogleIcon />
      Mit Google anmelden
    </button>
  );

  return (
    <div className={`min-h-screen flex items-center justify-center px-4 ${
      isDarkMode ? 'bg-neutral-950' : 'bg-stone-50'
    }`}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex w-12 h-12 rounded-xl bg-gradient-primary items-center justify-center shadow-glow mb-4">
            <Mail className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white tracking-tight">
            Odium Manager
          </h1>
          <p className="mt-1.5 text-sm text-neutral-500 dark:text-neutral-400">
            {subtitle}
          </p>
        </div>

        <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-6 sm:p-7 shadow-sm">
          {mode === 'picker' && accounts.length > 0 && (
            <div className="space-y-3">
              <div className="space-y-2 max-h-[22rem] overflow-y-auto pr-0.5">
                {accounts.map((account) => {
                  const isBusy = showBusyLabel && activeAccountId === account.id;
                  return (
                    <div
                      key={account.id}
                      className="w-full flex items-center gap-3 p-3.5 rounded-xl border border-neutral-200 dark:border-neutral-700 hover:border-primary-400 dark:hover:border-primary-400/70 hover:bg-primary-50/60 dark:hover:bg-neutral-800 transition-colors group"
                    >
                      <button
                        type="button"
                        onClick={() => selectAccount(account)}
                        disabled={submitting}
                        className="flex items-center gap-3 min-w-0 flex-1 text-left disabled:opacity-60"
                      >
                        <div className="w-12 h-12 rounded-full bg-gradient-primary text-white flex items-center justify-center text-lg font-semibold flex-shrink-0 shadow-sm">
                          {accountInitial(account.email)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-neutral-900 dark:text-white truncate">
                            {accountDisplayName(account.email)}
                          </p>
                          <p className="text-sm text-neutral-500 dark:text-neutral-400 truncate">
                            {account.email}
                            {account.authType === 'google' ? ' · Google' : ''}
                          </p>
                          {isBusy && (
                            <p className="text-xs text-primary-600 dark:text-primary-400 mt-0.5">
                              Anmelden…
                            </p>
                          )}
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={(e) => requestRemoveAccount(e, account)}
                        disabled={submitting}
                        className="p-2 rounded-lg text-neutral-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/50 opacity-70 group-hover:opacity-100 transition-opacity flex-shrink-0 disabled:opacity-40"
                        aria-label="Account entfernen"
                        title="Account entfernen"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>

              <button
                type="button"
                onClick={useDifferentAccount}
                disabled={submitting}
                className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-medium text-neutral-600 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-white transition-colors disabled:opacity-50"
              >
                <UserRound className="w-4 h-4" />
                Anderer Account
              </button>
            </div>
          )}

          {mode === 'form' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {googleEnabled ? (
                <>
                  {GoogleButton}
                  <div className="relative flex items-center gap-3 py-0.5">
                    <div className="flex-1 h-px bg-neutral-200 dark:bg-neutral-700" />
                    <span className="text-[11px] uppercase tracking-wide text-neutral-400">oder mit E-Mail</span>
                    <div className="flex-1 h-px bg-neutral-200 dark:bg-neutral-700" />
                  </div>
                </>
              ) : null}

              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">
                  E-Mail
                </label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                  <input
                    type="email"
                    autoComplete="username"
                    value={email}
                    onChange={(e) => handleEmailChange(e.target.value)}
                    className="input-field pl-10"
                    placeholder=""
                    required
                    autoFocus
                  />
                </div>
                {detectedProvider &&
                  (detectedProvider.id === 'gmail' ||
                    detectedProvider.id === 'webde' ||
                    (detectedProvider.id !== 'webde' &&
                      detectedProvider.id !== 'gmail' &&
                      detectedProvider.hint)) && (
                  <div className="mt-2.5 rounded-xl border border-amber-200 dark:border-amber-900/50 bg-amber-50/80 dark:bg-amber-950/30 px-3.5 py-3 space-y-1.5">
                    {detectedProvider.id === 'gmail' && (
                      <div className="text-[11px] text-amber-800/90 dark:text-amber-200/80 leading-relaxed space-y-1">
                        <p>
                          <strong>Gmail akzeptiert kein normales Google-Passwort</strong> im Feld unten.
                        </p>
                        <p>
                          {googleEnabled
                            ? 'Bitte „Mit Google anmelden“ nutzen oder ein App-Passwort eintragen.'
                            : 'Bitte ein App-Passwort unter myaccount.google.com/apppasswords erstellen und hier eintragen.'}
                        </p>
                      </div>
                    )}
                    {detectedProvider.id === 'webde' && (
                      <p className="text-[11px] text-amber-800/90 dark:text-amber-200/80 leading-relaxed">
                        Bitte bei web.de den POP3/IMAP-Abruf aktivieren.
                      </p>
                    )}
                    {detectedProvider.id !== 'webde' &&
                      detectedProvider.id !== 'gmail' &&
                      detectedProvider.hint && (
                      <p className="text-[11px] text-amber-800/90 dark:text-amber-200/80 leading-relaxed">
                        {detectedProvider.hint}
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">
                  Passwort
                </label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-field pl-10 pr-10"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200"
                    aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="rounded-xl border border-neutral-200 dark:border-neutral-700 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowAdvanced((v) => !v)}
                  className="w-full flex items-center justify-between gap-2 px-3.5 py-2.5 text-sm text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800/80 transition-colors"
                >
                  <span className="inline-flex items-center gap-2">
                    <Settings2 className="w-4 h-4 text-neutral-400" />
                    Erweitert
                  </span>
                  {showAdvanced ? (
                    <ChevronUp className="w-4 h-4 text-neutral-400" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-neutral-400" />
                  )}
                </button>

                {showAdvanced && (
                  <div className="px-3.5 pb-3.5 pt-1 space-y-3 border-t border-neutral-100 dark:border-neutral-800 bg-neutral-50/70 dark:bg-neutral-950/40">
                    <div className="flex flex-wrap gap-1.5">
                      {MAIL_PROVIDERS.filter((p) => p.id !== 'gmail').map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => applyPreset(p)}
                          className={`px-2 py-1 text-[11px] rounded-md border transition-colors ${
                            detectedProvider?.id === p.id
                              ? 'border-primary-400 bg-primary-50 text-primary-700 dark:bg-primary-950/40 dark:text-primary-300'
                              : 'border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-300 hover:border-primary-300'
                          }`}
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>

                    {detectedProvider?.hint && (
                      <p className="text-[11px] text-amber-700 dark:text-amber-300/90 leading-relaxed">
                        {detectedProvider.hint}
                      </p>
                    )}

                    <div className="space-y-2">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                        IMAP (Empfang)
                      </p>
                      <div>
                        <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">
                          IMAP-Host
                        </label>
                        <input
                          type="text"
                          value={mailForm.imapHost}
                          onChange={(e) => updateMailField('imapHost', e.target.value)}
                          className="input-field !py-2 text-sm"
                          placeholder="imap.web.de"
                          autoComplete="off"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">
                          IMAP-Port
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="65535"
                          value={mailForm.imapPort}
                          onChange={(e) => updateMailField('imapPort', e.target.value)}
                          className="input-field !py-2 text-sm"
                          placeholder="993"
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2 pt-1">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                        SMTP (Versand)
                      </p>
                      <div>
                        <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">
                          SMTP-Host
                        </label>
                        <input
                          type="text"
                          value={mailForm.smtpHost}
                          onChange={(e) => updateMailField('smtpHost', e.target.value)}
                          className="input-field !py-2 text-sm"
                          placeholder="smtp.web.de"
                          autoComplete="off"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">
                          SMTP-Port
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="65535"
                          value={mailForm.smtpPort}
                          onChange={(e) => updateMailField('smtpPort', e.target.value)}
                          className="input-field !py-2 text-sm"
                          placeholder="587"
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <button type="submit" disabled={submitting} className="btn-primary w-full mt-2">
                {showBusyLabel ? (
                  <>
                    <div className="spinner-sm mr-2" />
                    Anmelden…
                  </>
                ) : (
                  <>
                    <LogIn className="w-4 h-4 mr-2" />
                    Anmelden
                  </>
                )}
              </button>

              {accounts.length > 0 && (
                <button
                  type="button"
                  onClick={() => setMode('picker')}
                  className="w-full text-sm text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200 transition-colors"
                >
                  Zurück zu gespeicherten Accounts
                </button>
              )}
            </form>
          )}
        </div>
      </div>

      {accountToRemove && (
        <div className="fixed inset-0 z-[240] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-neutral-900/60 backdrop-blur-sm"
            onClick={() => setAccountToRemove(null)}
          />
          <div className="relative bg-white dark:bg-neutral-900 rounded-2xl shadow-xl w-full max-w-sm overflow-hidden border border-neutral-200 dark:border-neutral-800 scale-in">
            <div className="px-5 pt-5 pb-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-danger-50 dark:bg-danger-900/30 text-danger-600 dark:text-danger-400 flex items-center justify-center flex-shrink-0">
                  <Trash2 className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-neutral-900 dark:text-white">
                    Account entfernen?
                  </h3>
                  <p className="mt-1.5 text-sm text-neutral-600 dark:text-neutral-400">
                    „{accountDisplayName(accountToRemove.email) || accountToRemove.email}“ wirklich
                    aus der Liste entfernen? Du kannst dich später erneut anmelden.
                  </p>
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-neutral-100 dark:border-neutral-800 flex flex-row-reverse gap-2 bg-neutral-50/80 dark:bg-neutral-950/40">
              <button
                type="button"
                onClick={confirmRemoveAccount}
                className="inline-flex items-center justify-center rounded-lg bg-danger-600 hover:bg-danger-700 text-white text-sm font-medium px-3.5 py-2"
              >
                Entfernen
              </button>
              <button
                type="button"
                onClick={() => setAccountToRemove(null)}
                className="inline-flex items-center justify-center rounded-lg border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 text-sm font-medium px-3.5 py-2 hover:bg-white dark:hover:bg-neutral-800"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
